import { Button, ButtonVariant } from "components/Form/Button";
import { DisclosureCard } from "components/DisclosureCard";
import { parseKeyValueLines } from "lib/key-value-lines";
import { getOrbDetailPairs, OrbDetailValue } from "lib/orb-qr";
import { PrismicCollectionDetailField } from "types";

interface DetailGroup {
  title: string;
  fields: PrismicCollectionDetailField[];
  values: OrbDetailValue[];
}

function LabelValues({ fields, values }: Omit<DetailGroup, "title">) {
  return (
    <dl className="divide-y divide-f4f7ff">
      {values.map(({ label_id, value }) => {
        const field = fields.find((item) => item.label_id?.trim() === label_id);
        const displayValue =
          field?.label_type === "dropdown"
            ? parseKeyValueLines(field.dropdown_options).find(
                (option) => option.value === value || option.key === value
              )?.key || value
            : value;
        return (
          <div key={label_id} className="grid grid-cols-2 gap-4 py-3 text-sm">
            <dt className="break-words text-858494">
              {field?.label_title || label_id.replace(/_/g, " ")}
            </dt>
            <dd className="break-words text-right font-medium text-191c20">
              {displayValue}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

export function RtmsSectionDetails({
  sharedDetails,
  sharedFields,
  sectionGroups,
  onEditShared,
  onEditSection,
  disabled,
}: {
  sharedDetails: unknown;
  sharedFields: PrismicCollectionDetailField[];
  sectionGroups: DetailGroup[];
  onEditShared: () => void;
  onEditSection?: () => void;
  disabled: boolean;
}) {
  // Use the same applicable shared values as the QR, not campaign-wide storage.
  const sharedValues = getOrbDetailPairs(sharedDetails).map((pair) => {
    const separator = pair.indexOf(",");
    return {
      label_id: pair.slice(0, separator),
      value: pair.slice(separator + 1),
    };
  });
  return (
    <DisclosureCard
      title="Collection details"
      description="Review the details applied to these captures"
    >
      <section aria-label="Shared collection details">
        <h2 className="text-sm font-semibold text-191c20">
          Shared across this Work Task
        </h2>
        {sharedValues.length ? (
          <LabelValues fields={sharedFields} values={sharedValues} />
        ) : (
          <p className="mt-2 text-sm text-858494">No shared details added.</p>
        )}
        <Button
          variant={ButtonVariant.FLAT}
          className="mt-3 w-full bg-f4f7ff text-sm"
          disabled={disabled}
          onClick={onEditShared}
        >
          Edit shared details
        </Button>
      </section>
      {sectionGroups.map(
        (group, index) =>
          group.fields.length > 0 && (
            <section key={index} aria-label={group.title}>
              <h2 className="text-sm font-semibold text-191c20">
                {group.title}
              </h2>
              {group.values.length ? (
                <LabelValues fields={group.fields} values={group.values} />
              ) : (
                <p className="mt-2 text-sm text-858494">
                  No details selected yet.
                </p>
              )}
            </section>
          )
      )}
      {onEditSection && (
        <Button
          variant={ButtonVariant.FLAT}
          className="w-full bg-f4f7ff text-sm"
          disabled={disabled}
          onClick={onEditSection}
        >
          Edit section details
        </Button>
      )}
    </DisclosureCard>
  );
}
